<template>
  <div class="request-page">
    <div class="request-container">
      <div class="request-card">
        <div class="header">
          <router-link to="/login" class="back-link">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to login
          </router-link>
          <h2>Request Access</h2>
          <p>Fill out the form below to request a new account.</p>
        </div>

        <form @submit.prevent="handleSubmit" v-if="!submitted">
          <div class="form-grid">
            <div class="input-group">
              <label>Full Name</label>
              <input type="text" v-model="form.full_name" required placeholder="John Doe" />
            </div>
            <div class="input-group">
              <label>Desired Username</label>
              <input type="text" v-model="form.username" required placeholder="johndoe" @input="validateUsername" />
              <small v-if="usernameError" class="error-hint">{{ usernameError }}</small>
            </div>
            <div class="input-group">
              <label>Email Address</label>
              <input type="email" v-model="form.email" required placeholder="john@example.com" />
            </div>
            <div class="input-group">
              <label>Requested Password</label>
              <input type="password" v-model="form.requested_password" required placeholder="••••••••" />
              <small class="info-hint">This will be your account password once approved.</small>
            </div>
          </div>
          
          <div class="input-group full-width">
            <label>Reason for Access</label>
            <textarea v-model="form.reason" rows="3" placeholder="I need access to report IT issues..."></textarea>
          </div>

          <button type="submit" class="submit-btn" :disabled="loading || !!usernameError">
            <span v-if="!loading">Submit Request</span>
            <div v-else class="spinner"></div>
          </button>

          <p v-if="error" class="error-msg">{{ error }}</p>
        </form>

        <div v-else class="success-state">
          <div class="success-icon">✓</div>
          <h3>Request Sent!</h3>
          <p>Permintaan akses sudah dikirim. Admin akan meninjau request Anda. Silakan hubungi Admin jika butuh persetujuan cepat.</p>
          <router-link to="/login" class="login-btn">Return to Login</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import axios from 'axios'

const API_URL = (import.meta.env.VITE_API_URL || 'https://it-ticketing-api.maulanarofiq72.workers.dev').replace(/\/$/, '')

const form = reactive({
  full_name: '',
  username: '',
  email: '',
  requested_password: '',
  reason: ''
})

const loading = ref(false)
const submitted = ref(false)
const error = ref('')
const usernameError = ref('')

const validateUsername = () => {
  if (form.username.includes(' ')) {
    usernameError.value = 'Username cannot contain spaces'
  } else {
    usernameError.value = ''
  }
}

const handleSubmit = async () => {
  if (usernameError.value) return
  loading.value = true
  error.value = ''
  try {
    await axios.post(`${API_URL}/request-access`, form)
    submitted.value = true
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to submit request.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.request-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  padding: 2rem;
}
.request-container {
  width: 100%;
  max-width: 600px;
}
.request-card {
  background: white;
  padding: 3rem;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
}
.header { margin-bottom: 2rem; }
.back-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}
h2 { font-size: 2rem; color: #1e293b; margin-bottom: 0.5rem; }
p { color: #64748b; }

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.input-group { display: flex; flex-direction: column; gap: 0.5rem; }
.full-width { grid-column: span 2; }

label { font-size: 0.875rem; font-weight: 600; color: #475569; }
input, textarea {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
}
input:focus, textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.error-hint { color: #ef4444; font-size: 0.75rem; }
.info-hint { color: #94a3b8; font-size: 0.75rem; }

.submit-btn {
  width: 100%;
  padding: 1rem;
  background-color: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.submit-btn:hover { background-color: #1d4ed8; }
.submit-btn:disabled { background-color: #94a3b8; cursor: not-allowed; }

.error-msg { color: #ef4444; text-align: center; margin-top: 1rem; font-weight: 500; }

.success-state { text-align: center; }
.success-icon {
  width: 80px; height: 80px; background: #f0fdf4; color: #16a34a;
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 2.5rem; margin: 0 auto 2rem;
}
.login-btn {
  display: inline-block; margin-top: 2rem; padding: 0.75rem 2rem;
  background: #2563eb; color: white; text-decoration: none; border-radius: 8px; font-weight: 600;
}
.spinner {
  width: 24px; height: 24px; border: 3px solid rgba(255,255,255,0.3);
  border-radius: 50%; border-top-color: white; animation: spin 0.8s linear infinite; margin: 0 auto;
}
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .form-grid { grid-template-columns: 1fr; }
  .full-width { grid-column: span 1; }
}
</style>
